import { forwardRef } from 'react';
import { Box as MuiBox, BoxProps as MuiBoxProps } from '@mui/material';

/**
 * Box component - A flexible layout primitive for building responsive layouts.
 *
 * This is a thin wrapper around Material-UI's Box component that provides:
 * - Flexbox layout capabilities (70% of mobile app usage)
 * - Grid layout support
 * - Spacing utilities (padding, margin, gap)
 * - Responsive props with breakpoints
 * - Position and overlay capabilities
 * - Theme-aware styling via sx prop
 *
 * @example
 * // Flex row with gap (most common pattern)
 * <Box display="flex" alignItems="center" gap={1}>
 *   <Button>Action 1</Button>
 *   <Button>Action 2</Button>
 * </Box>
 *
 * @example
 * // Header pattern with space-between
 * <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
 *   <Typography variant="h6">Title</Typography>
 *   <IconButton>...</IconButton>
 * </Box>
 *
 * @example
 * // Responsive grid
 * <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
 *   {items}
 * </Box>
 *
 * @example
 * // Overlay pattern
 * <Box position="absolute" top={0} left={0} width="100%" height="100%">
 *   <LoadingBackdrop />
 * </Box>
 */
export type BoxProps = MuiBoxProps;

/**
 * Box component - The fundamental layout primitive.
 *
 * A flexible container that supports flexbox, grid, spacing, and responsive layouts.
 * This is a thin wrapper around MUI Box that preserves all Material-UI capabilities
 * while providing a consistent import path through the ui-library.
 */
export const Box = forwardRef<HTMLDivElement, BoxProps>((props, ref) => (
  <MuiBox ref={ref} {...props} />
));

Box.displayName = 'Box';
